import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './Style.css';
import ModalTrigger from '../../../shared/modal/ModalTrigger';
import { convertScaleValues } from '../../../shared/util/assessments';

type DialSliderProps = {
  questionTitle: string;
  modalText?: string;
  onSetValue: (value: { id: string; value: string }) => void;
  onOpenModal?: () => void;
  questionid: string;
  assessmentValues: Record<string, unknown>;
  width: string;
  scale?: number;
  textValue: string[];
};

export function DialSlider({
  questionTitle,
  modalText,
  onSetValue,
  onOpenModal,
  questionid,
  assessmentValues,
  width,
  scale,
  textValue,
}: DialSliderProps) {
  const [rotation, setRotation] = useState<number>(0);
  const [rangeValue, setRangeValue] = useState<string>('');
  const { t } = useTranslation();

  const converValueToRotation = (value) => {
    const scaleValue = scale === 5 ? convertScaleValues(value.toString()) : value;
    if (scaleValue === 5) {
      return 0;
    }
    let newRotation = -100 + (180 / 10) * Number(scaleValue);
    if (newRotation >= 360) {
      newRotation = -360;
    }
    return newRotation;
  };

  useEffect(() => {
    if (assessmentValues[questionid]) {
      setRangeValue(assessmentValues[questionid].toString());
      const newRotation = converValueToRotation(assessmentValues[questionid]);
      setRotation(newRotation);
    } else {
      setRotation(0);
      if (scale === 5) {
        setRangeValue('3');
      } else {
        setRangeValue('5');
      }
      onSetValue({
        id: questionid,
        value: scale === 5 ? '3' : '5',
      });
    }
  }, []);

  const onSlide = (event) => {
    const { value } = event.target;
    const scaleValue = scale === 5 ? convertScaleValues(value) : value;
    if (scaleValue === 5 || scaleValue === '5') {
      setRotation(0);
    } else {
      const degree = -100 + (180 / 10) * Number(scaleValue);
      let newRotation = degree;
      if (newRotation >= 360) {
        newRotation = -360;
      }
      setRotation(newRotation);
    }

    setRangeValue(value);
    onSetValue({ id: questionid, value });
  };

  return (
    <div className={`req question_block${width} dial_slider_container`}>
      <fieldset id={questionid} data-input-type="dial-slider">
        <legend className="assessment_question_title">{questionTitle}</legend>
        {modalText && (
          <ModalTrigger
            modalText={modalText}
            onOpenModal={onOpenModal}
            question={questionid}
            questionTitle={questionTitle}
          />
        )}
        <div className="answer_block fs-exclude">
          <div
            className={`dialSlider assessment_slider${width}`}
            id={`${questionid}-dial-slider-container`}
          >
            <div className="sliderValues" id={`${questionid}-slider-values`}>
              <div>{textValue[0]} </div>
              <div>{textValue[1]}</div>
            </div>
            <div className="dial">
              <div className="needle" style={{ transform: `rotate(${rotation}deg)` }} />
            </div>
            <div
              className={`range-container ${
                scale === 5 ? 'scale-5' : 'scale-10'
              } value-${rangeValue}`}
            >
              <input
                type="range"
                min="1"
                max={scale === 5 ? '5' : '10'}
                value={rangeValue}
                onChange={onSlide}
                id={`${questionid}_slider`}
                aria-valuetext={rangeValue}
                aria-label={`${questionTitle} ${t('slider.description.from')} ${textValue[0]} ${t('slider.description.to')} ${textValue[1]}`}
                data-uw-ignore-s3
              />
            </div>
            <label
              htmlFor={`${questionid}_slider`}
              className="valueHandle"
              id={`${questionid}_value_handle`}
            >
              {rangeValue}
            </label>
          </div>
        </div>
      </fieldset>
    </div>
  );
}
